import React, { useMemo } from "react";
import { motion } from "motion/react";
import {
  Trophy,
  TrendingUp,
  TrendingDown,
  Calendar,
  Flame,
  Snowflake,
} from "lucide-react";
import { parseLocalDate } from "../../utils/date";

const DashboardHero = ({ team, upcomingGames, finishedGames }) => {
  // Calculate win streak
  const streak = useMemo(() => {
    if (!finishedGames?.length) return { type: null, count: 0 };

    let count = 0;
    let type = null;

    for (let i = 0; i < finishedGames.length; i++) {
      const game = finishedGames[i];
      const isHome = game.team1?.id === team.id;
      const myScore = isHome ? game.team1_score : game.team2_score;
      const oppScore = isHome ? game.team2_score : game.team1_score;
      const won = myScore > oppScore;

      if (i === 0) {
        type = won ? "W" : "L";
        count = 1;
      } else if ((won && type === "W") || (!won && type === "L")) {
        count++;
      } else {
        break;
      }
    }

    return { type, count };
  }, [finishedGames, team?.id]);

  // Calculate next game countdown
  const nextGame = upcomingGames?.[0];
  const daysUntilNextGame = useMemo(() => {
    if (!nextGame?.game_date) return null;
    const gameDate = parseLocalDate(nextGame.game_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = gameDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [nextGame]);

  // Calculate team stats
  const teamStats = useMemo(() => {
    if (!finishedGames?.length) return null;

    let totalPoints = 0;
    let totalOppPoints = 0;

    finishedGames.forEach((game) => {
      const isHome = game.team1?.id === team.id;
      const myScore = isHome ? game.team1_score : game.team2_score;
      const oppScore = isHome ? game.team2_score : game.team1_score;
      totalPoints += myScore || 0;
      totalOppPoints += oppScore || 0;
    });

    const gamesPlayed = finishedGames.length;
    const ppg = (totalPoints / gamesPlayed).toFixed(1);
    const oppPpg = (totalOppPoints / gamesPlayed).toFixed(1);
    const differential = (ppg - oppPpg).toFixed(1);

    return { ppg, oppPpg, differential };
  }, [finishedGames, team?.id]);

  // Generate gradient style
  const gradientStyle = useMemo(() => {
    if (!team?.primary_color && !team?.secondary_color) {
      return "from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900";
    }

    const primary = team.primary_color ? `#${team.primary_color}` : "#f97316";
    const secondary = team.secondary_color
      ? `#${team.secondary_color}`
      : "#f59e0b";

    if (team.gradient) {
      return {
        background: `linear-gradient(135deg, ${primary}15 0%, ${secondary}15 100%)`,
      };
    }

    return {
      background: `linear-gradient(135deg, ${primary}10 0%, ${primary}05 100%)`,
    };
  }, [team]);

  const accentColor = team?.primary_color
    ? `#${team.primary_color}`
    : "#f97316";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl border border-orange-100 dark:border-orange-900/30 p-6 sm:p-8"
      style={typeof gradientStyle === "string" ? {} : gradientStyle}
    >
      {typeof gradientStyle === "string" && (
        <div
          className={`absolute inset-0 bg-gradient-to-br ${gradientStyle}`}
        />
      )}

      {/* Decorative gradient overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          background: `radial-gradient(circle at 20% 50%, ${accentColor} 0%, transparent 50%)`,
        }}
      />

      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
          {/* Team Logo */}
          {team?.logo_url ? (
            <motion.img
              whileHover={{ scale: 1.05, rotate: 5 }}
              src={team.logo_url}
              alt={team.name}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shadow-lg ring-4 ring-white dark:ring-gray-800"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center text-5xl shadow-lg ring-4 ring-white dark:ring-gray-800">
              🏀
            </div>
          )}

          {/* Team Info */}
          <div className="flex-1">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 dark:text-white mb-2">
              {team?.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm sm:text-base">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">
                  Record:
                </span>
                <span className="font-bold text-gray-900 dark:text-white text-lg">
                  {team?.wins || 0}–{team?.losses || 0}
                </span>
              </div>

              {team?.wins + team?.losses > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">
                      Win %:
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {((team.wins / (team.wins + team.losses)) * 100).toFixed(
                        0,
                      )}
                      %
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Season Badge */}
          <div className="px-4 py-2 bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 shadow-sm">
            <p className="text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Season
            </p>
            <p className="font-bold text-gray-900 dark:text-white">
              {team?.season?.name}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Streak Indicator */}
          {streak.count > 0 && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              className={`bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 transition-all ${
                streak.type === "W"
                  ? "border-emerald-200 dark:border-emerald-800"
                  : "border-red-200 dark:border-red-800"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {streak.type === "W" ? (
                  <Flame className="text-orange-500" size={20} />
                ) : (
                  <Snowflake className="text-blue-400" size={20} />
                )}
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  {streak.type === "W" ? "Hot Streak" : "Cold Streak"}
                </p>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">
                {streak.count}
                <span className="text-lg ml-1 text-gray-400">
                  {streak.type}
                </span>
              </p>
            </motion.div>
          )}

          {/* Next Game Countdown */}
          {nextGame && daysUntilNextGame !== null && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-blue-200 dark:border-blue-800 transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-blue-500" size={20} />
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  Next Game
                </p>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">
                {daysUntilNextGame === 0
                  ? "Today"
                  : daysUntilNextGame === 1
                    ? "Tomorrow"
                    : `${daysUntilNextGame}d`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                vs{" "}
                {nextGame.team1?.id === team.id
                  ? nextGame.team2?.name
                  : nextGame.team1?.name}
              </p>
            </motion.div>
          )}

          {/* Points Per Game */}
          {teamStats && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-purple-200 dark:border-purple-800 transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="text-purple-500" size={20} />
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  PPG
                </p>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">
                {teamStats.ppg}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Opp: {teamStats.oppPpg}
              </p>
            </motion.div>
          )}

          {/* Point Differential */}
          {teamStats && (
            <motion.div
              whileHover={{ scale: 1.02 }}
              className={`bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 transition-all ${
                parseFloat(teamStats.differential) > 0
                  ? "border-emerald-200 dark:border-emerald-800"
                  : "border-red-200 dark:border-red-800"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {parseFloat(teamStats.differential) > 0 ? (
                  <TrendingUp className="text-emerald-500" size={20} />
                ) : (
                  <TrendingDown className="text-red-500" size={20} />
                )}
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  Differential
                </p>
              </div>
              <p
                className={`text-3xl font-black ${
                  parseFloat(teamStats.differential) > 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {parseFloat(teamStats.differential) > 0 ? "+" : ""}
                {teamStats.differential}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                per game
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardHero;
